<template>
  <div class="modal-overlay">
    <div class="modal-background" @click="$emit('close')" />
    <div class="modal">
      <div class="title-close">
        <h1 class="modal-title">{{ adminText('organizationAll.customize_report_9158667d', 'Customize report') }}</h1>
        <span class="material-icons" @click="$emit('close')"> close </span>
      </div>

      <!-- Report Type Tabs -->
      <div class="report-tabs">
        <button
          class="tab-button"
          :class="{ active: form.periodType === 'Sales Report' }"
          @click="form.periodType = 'Sales Report'"
        >
          {{ adminText('organizationAll.sales_report_0f89ab2f', 'Sales Report') }}
        </button>
        <button
          class="tab-button"
          :class="{ active: form.periodType === 'Performances Report' }"
          @click="form.periodType = 'Performances Report'"
        >
          {{ adminText('organizationAll.performances_report_bcb610c4', 'Performances Report') }}
        </button>
      </div>

      <p class="report-description">
        <template v-if="form.periodType === 'Sales Report'">
          {{ adminText('organizationAll.this_report_shows_ticket_sales_based_on_the_d_cc5048b9', 'This report shows ticket sales based on the date of purchase. It includes') }}
          <strong>{{ adminText('organizationAll.all_tickets_sold_within_the_selected_period_bcb16d5e', 'all tickets sold within the selected period') }}</strong>{{ adminText('organizationAll.regardless_of_when_the_event_or_performance_t_146e07b6', ', regardless of when the event or performance takes place.') }}
        </template>
        <template v-else>
          {{ adminText('organizationAll.this_report_shows_ticket_sales_grouped_by_per_ec0b375e', 'This report shows ticket sales grouped by performances scheduled within the selected period.') }}
          <strong> {{ adminText('organizationAll.only_performances_that_take_place_in_the_sele_b5c7f67b', 'Only performances that take place in the selected date range are included') }}</strong>{{ adminText('organizationAll.regardless_of_when_the_tickets_were_sold_1029efba', ', regardless of when the tickets were sold.') }}
        </template>
      </p>

      <div class="form-grid">
        <date-field v-model="form.from" :label="adminText('organizationAll.from_3f66052a', 'From')" :max="form.to" />
        <date-field v-model="form.to" :label="adminText('organizationAll.to_ae79ea1e', 'To')" :min="form.from" />
        <select-field-multiple-with-all
          v-model="form.eventTemplateIds"
          :label="adminText('organizationAll.play_title_6c1ad555', 'Play title')"
          :items="computedPlayTitles"
          item-select="id"
          item-render="title"
          :max-visible-selections="3"
        />
        <select-field-multiple-with-all
          v-model="form.eventIds"
          :label="adminText('organizationAll.performance_63c90455', 'Performance')"
          :items="computedPerformances"
          item-select="id"
          item-render="title"
          :max-visible-selections="3"
        />
        <select-field-multiple-with-all
          v-model="form.venues"
          :label="adminText('organizationAll.venue_67cd4061', 'Venue')"
          :items="computedVenues"
          :max-visible-selections="3"
          style="grid-column: 1 / -1"
        />
        <select-field
          v-model="form.saleType"
          :label="adminText('organizationAll.sales_method_bd806249', 'Sales method')"
          :items="organizationOptions(['All', 'Offline sales', 'Online sales', 'CoreEvent ticket offices'])"
          item-render="text"
          item-select="value"
        />
        <select-field-multiple-with-all
          v-model="form.ticketSellerIds"
          :label="adminText('organizationAll.ticket_office_location_caab0b77', 'Ticket office location')"
          :items="ticketSellers"
          item-select="id"
          item-render="name"
          :disabled="['Online sales', 'CoreEvent ticket offices'].includes(form.saleType)"
        />
        <select-field
          v-model="form.paymentType"
          :label="adminText('organizationAll.payment_method_0e55fb3b', 'Payment method')"
          :items="organizationOptions(['All', 'Cash', 'Card payment'])"
          item-render="text"
          item-select="value"
          :disabled="['Online sales', 'CoreEvent ticket offices'].includes(form.saleType)"
          style="grid-column: 1 / -1"
        />
        <select-field
          v-model="form.reportType"
          :label="adminText('organizationAll.report_type_0ca4fce1', 'Report type')"
          :items="organizationOptions(['Ticket office report', 'Performance report'])"
          item-render="text"
          item-select="value"
          style="grid-column: 1 / -1"
        />
      </div>

      <div class="buttons">
        <main-button color="primary" :text="adminText('organizationAll.generate_report_4d8abbdc', 'Generate report')" @click="emitGenerate" />
      </div>
      <simple-snackbar :open.sync="snackbarOpen" :message-prop="organizationText(errorMessage)" color="error" />
    </div>
  </div>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import moment from 'moment'
import { GET_ORGANIZATION_EVENT_TEMPLATES } from '@/graphql/eventTemplate.graphql'
import { GET_BOX_OFFICE_TICKET_SELLERS } from '@/graphql/user.graphql'
import { uniqBy } from 'lodash'
import SelectFieldMultipleWithAll from '@/components/shared/inputs/select-field-mutliple-with-all.vue'
import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'

export default {
  mixins: [OrganizationLocalization],
  name: 'reports-customize-modal',
  components: {
    SelectFieldMultipleWithAll,
    SimpleSnackbar,
  },
  props: {
    organizationSlug: String,
  },
  data() {
    return {
      form: {
        from: null,
        to: null,
        eventTemplateIds: [],
        eventIds: [],
        venues: [],
        saleType: 'All',
        ticketSellerIds: [],
        paymentType: 'All',
        periodType: 'Sales Report',
        reportType: 'Ticket office report',
      },
      allEvents: [],
      eventTemplates: [],
      ticketSellers: [],
      snackbarOpen: false,
      errorMessage: '',
    }
  },
  computed: {
    computedPlayTitles() {
      const uniqueEventTemplates = uniqBy(this.eventTemplates, 'id')

      return uniqueEventTemplates.map(et => {
        return {
          id: et.id,
          title: et.title,
        }
      })
    },
    computedPerformances() {
      let allPerformances = this.allEvents
      if (this.form.eventTemplateIds.length) {
        allPerformances = allPerformances.filter(p =>
          this.form.eventTemplateIds.includes(p.eventTemplateId),
        )
      }

      return allPerformances.map(p => {
        return {
          id: p.id,
          title: `${p.title} - ${moment(p.from).format('DD.MM.YYYY.')}`,
        }
      })
    },
    computedVenues() {
      const venues = new Set()
      for (const ev of this.allEvents) {
        if (ev.location && ev.location.venue) venues.add(ev.location.venue)
      }

      return Array.from(venues)
    },
  },
  watch: {
    'form.saleType': {
      handler(newVal) {
        if (['Online sales', 'CoreEvent ticket offices'].includes(newVal)) {
          this.form.ticketSellerIds = []
          this.form.paymentType = 'All'
        }
      },
    },
    'form.eventTemplateIds': {
      handler(newVal) {
        this.form.eventIds = this.form.eventIds.filter(id =>
          this.computedPerformances.some(p => p.id === id),
        )
      },
    },
  },
  methods: {
    emitGenerate() {
      const fromSet = !!this.form.from
      const toSet = !!this.form.to
      if ((fromSet && !toSet) || (!fromSet && toSet)) {
        this.errorMessage = 'Please set both From and To dates or leave both empty.'
        this.snackbarOpen = true

        return
      }
      const payload = { ...this.form }
      if (payload.from) {
        payload.from = moment(payload.from).startOf('day').toISOString()
      }
      if (payload.to) {
        payload.to = moment(payload.to).endOf('day').toISOString()
      }
      if (payload.eventTemplateIds.length === this.computedPlayTitles.length) {
        payload.eventTemplateIds = undefined
      }
      if (payload.eventIds.length === this.computedPerformances.length) {
        payload.eventIds = undefined
      }
      if (payload.venues.length === this.computedVenues.length) {
        payload.venues = undefined
      }
      // Narrowing down to specific ticket offices makes the report about those offices,
      // so report them as offline sales instead of mixing in every online sale.
      if (
        payload.saleType === 'All' &&
        payload.ticketSellerIds.length &&
        payload.ticketSellerIds.length < this.ticketSellers.length
      ) {
        payload.saleType = 'Offline sales'
      }
      this.$emit('generate', payload)
    },
  },
  apollo: {
    getOrganizationEventTemplates: {
      query: GET_ORGANIZATION_EVENT_TEMPLATES,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
        }
      },
      result({ data, loading }) {
        if (loading) return
        const list = data.getOrganizationEventTemplates || []
        this.eventTemplates = list
        this.allEvents = list.flatMap(et => et.events || [])

        this.form.eventTemplateIds = this.computedPlayTitles.map(pt => pt.id)
        this.form.eventIds = this.computedPerformances.map(p => p.id)
        this.form.venues = this.computedVenues
      },
    },
    getBoxOfficeTicketSellers: {
      query: GET_BOX_OFFICE_TICKET_SELLERS,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
        }
      },
      result({ data, loading }) {
        if (loading) return
        const fetchedTicketSellers = (data.getBoxOfficeTicketSellers || []).map(ts => {
          return {
            ...ts,
            name: `${ts.firstName} ${ts.lastName}`,
          }
        })
        this.ticketSellers = fetchedTicketSellers
        this.form.ticketSellerIds = this.ticketSellers.map(ts => ts.id)
      },
    },
  },
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
}
.modal-background {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #00000050;
}
.modal {
  text-align: left;
  background-color: white;
  width: 100%;
  position: relative;
  max-width: 40.75rem;
  max-height: 90vh;
  padding: 2rem;
  border-radius: 20px;
  overflow-y: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.modal::-webkit-scrollbar {
  display: none;
}
.modal-title {
  color: #0277bd;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.0375rem;
}
@media screen and (max-width: 700px) {
  .modal {
    width: 100%;
    max-width: 100%;
    height: 100%;
    border-radius: 0;
  }
}
.title-close {
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.material-icons {
  cursor: pointer;
}
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 1rem;
}
@media screen and (max-width: 600px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}
.buttons {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
}

/* Report Tabs Styling */
.report-tabs {
  display: flex;
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 0;
  margin-bottom: 1rem;
  gap: 0;
}

.tab-button {
  flex: 1;
  padding: 8px 16px;
  border: none;
  background-color: #f5f5f5;
  color: #0277bd;
  font-family: Raleway, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.27px;
  text-transform: uppercase;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tab-button:hover {
  opacity: 0.8;
}

.tab-button.active {
  background-color: #0277bd;
  color: white;
}

.report-description {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px;
  line-height: 1.5;
  color: #979797;
  margin-bottom: 1.5rem;
}

.report-description strong {
  font-family: 'Source Sans Pro', sans-serif;
  font-weight: 700;
  color: #979797;
}

/* Mobile responsive adjustments */
@media screen and (max-width: 600px) {
  .tab-button {
    font-size: 14px;
    padding: 8px 12px;
    letter-spacing: -0.2px;
  }

  .report-description {
    font-size: 14px;
  }

  .modal {
    padding: 1.5rem;
  }

  .modal-title {
    font-size: 1.1rem;
  }
}

@media screen and (max-width: 400px) {
  .tab-button {
    font-size: 12px;
    padding: 8px 8px;
  }

  .report-tabs {
    margin-bottom: 0.75rem;
  }

  .report-description {
    font-size: 13px;
    margin-bottom: 1rem;
  }
}
</style>
